import Cookies from 'js-cookie';
import SvgIcon from '@png/npm-svg-icon';
import ApiManager from '../api/manager';
import utils from '../utils';
import RealTimePrice from './real-time-price';
import RealTimeStock from './real-time-stock';
import Incrementer from './incrementer';
import DataLayerController from './DataLayerController';

/**
 * Class product Favorite module
 * @class
 * @author Max Swinkels <max.swinkels@pangaea.nl>
 *
 * See the manual for more information and implementation
 * https://pangaeanl.atlassian.net/wiki/spaces/PD/pages/1159135233/Favorieten+module\
 *
 * issue
 * https://pangaeanl.atlassian.net/browse/PNGI-449
 */

const setFavoriteCookie = array => {
  Cookies.set('png_productFavorite', array);
};

const getFavoriteCookie = () => {
  const favoriteListArray = Cookies.get('png_productFavorite');

  if (favoriteListArray) {
    return JSON.parse(decodeURI(favoriteListArray));
  }

  // set emty array to prevent null errors
  setFavoriteCookie([]);

  return [];
};

class ProductFavorite {
  constructor(options = {}) {
    this.config = {
      toggleModuleList: () => document.querySelectorAll('[data-module="ProductFavoriteToggle"]'),
      listModule: document.querySelector('[data-module="ProductFavoritesList"]'),
      listEmptyModule: document.querySelector('[data-module="ProductFavoritesListEmptyMessage"]'),
      amountModule: document.querySelectorAll('[data-module="ProductFavoritesAmount"]'),
      favoriteListArray: [],
      hasPreferenceObject: false,
      debounce: null,
      debounceTimeOut: 300,
      token: Cookies.get('token'),
      ...options
    };
    this.apiManager = new ApiManager();
  }

  // get user preference and return array of users productFavorites
  getFavorites() {
    return this.apiManager
      .get('/api/v2/preference/user?key=productFavorites')
      .then(response => {
        // return null if user preferences is empty
        if (!response.length) {
          return null;
        }

        // return empty array if response has nu data
        if (!response[0].value) {
          return [];
        }

        // decode and parse data to json, then return
        return JSON.parse(decodeURI(response[0].value));
      })
      .catch(e => {
        console.error(e);
      });
  }

  // update user preferences object in database with new data
  updateFavoritesList(array) {
    const { token } = this.config;

    // debounce to prevent multiple API calls on spam click
    clearTimeout(this.config.debounce);
    this.config.debounce = window.setTimeout(() => {
      // convert array to encoded json string if not empty
      const json = array.length ? encodeURI(JSON.stringify(array)) : '';

      // update the cookie with the new encoded data
      setFavoriteCookie(json);

      // check if user is logged in, then run the process of the database update
      if (token) {
        // if the hasPreferenceObject is false, create new user preference object in databsae. Otherwise update it with new value
        if (this.config.hasPreferenceObject) {
          const patchValue = JSON.parse(`[{"op":"replace","path":"/value","value":"${json}"}]`);
          this.apiManager
            .patch('/api/v2/preference/user/productFavorites', patchValue)
            .then(response => response)
            .catch(e => {
              console.error(e);
            });
        } else {
          const postValue = JSON.parse(
            `{"key":"productFavorites","value":"${json}","type":"string"}`
          );
          this.apiManager
            .post('/api/v2/preference/user', postValue)
            .then(response => response)
            .then(() => {
              this.config.hasPreferenceObject = true;
            })
            .catch(e => {
              console.error(e);
            });
        }
      }
    }, this.config.debounceTimeOut);
  }

  // adds new item to the favoritesList
  addFavorite(productItemcode, _element) {
    const element = _element;
    const { favoriteListArray } = this.config;
    const itemIndex = favoriteListArray.indexOf(productItemcode);

    if (itemIndex > -1) {
      return;
    }

    element.classList.add('is-active');
    element.dataset.tooltip = element.dataset.removeTooltip || 'Remove from favorites';

    favoriteListArray.push(productItemcode);
    this.config.favoriteListArray = favoriteListArray;
    this.updateFavoritesList(favoriteListArray);

    window.dispatchEvent(
      new CustomEvent('toast', {
        detail: {
          type: 'success',
          text: element.dataset.addedToastMessage || 'Product added to favorites'
        }
      })
    );
  }

  // remove item from the favoritesList
  removeFavorite(productItemcode, _element) {
    const element = _element;
    const { favoriteListArray, listModule, listEmptyModule } = this.config;
    const itemIndex = favoriteListArray.indexOf(productItemcode);

    if (itemIndex === -1) {
      return;
    }

    element.classList.remove('is-active');
    element.dataset.tooltip = element.dataset.addTooltip || 'Add to favorites';

    favoriteListArray.splice(itemIndex, 1);
    this.config.favoriteListArray = favoriteListArray;
    this.updateFavoritesList(favoriteListArray);

    if (listModule) {
      // if product is removed from the favorite list, remove the element.
      const listElement = element.closest('[data-product-favorites-list-item]');
      if (listElement) {
        listElement.parentNode.removeChild(listElement);
      }

      // if favorite list is empty, display emty message module
      if (listEmptyModule && !favoriteListArray.length) {
        listEmptyModule.classList.remove('hidden');
      }
    }
  }

  renderFavoriteProductList() {
    const { listModule, listEmptyModule, favoriteListArray } = this.config;

    const element = listModule;

    if (!element) {
      return;
    }

    element.classList.add('is-loading');

    if (favoriteListArray.length) {
      const parameters = {
        itemcode: favoriteListArray.join(','), // makes a string of all itemcodes comma-separated
        all: true, // also shows the child product if the itemcode is a child product
        template: 'FavoriteProducts', // indicates the name of the file in /views/api
        sort: 'sorted', // no idea what it does
        omitfilterdata: 1, // no idea what it does
        items: 9999
      };

      // creates an url with all parameters build into the url
      const url = `/api/v2/catalog/product${utils.buildUrlParameters(parameters)}`;

      this.apiManager
        .get(url, 'text/html')
        .then(html => {
          // places the api html in the placeholder and removes the loader
          element.innerHTML = html;
          element.classList.remove('is-loading');

          // trigger all related modules
          new SvgIcon().init();
          new RealTimePrice().init();
          new RealTimeStock().init();
          new Incrementer().init();
          new DataLayerController().init();

          // eslint-disable-next-line no-undef
          cart.addEventListener();

          // trigger render favorite toggles
          this.renderFavoriteToggles();
        })
        .catch(e => {
          element.innerHTML = element.dataset.errorMessage || 'favorites not available.';
          element.classList.remove('is-loading');
          element.classList.add('is-error');
          console.error(e);
        });
    } else {
      // display list is empty module
      if (listEmptyModule) {
        listEmptyModule.classList.remove('hidden');
      }
      element.classList.remove('is-loading');
    }
  }

  // shows the number of favorite in the menu badge
  renderFavoriteAmount() {
    const { amountModule, favoriteListArray } = this.config;

    if (!amountModule) {
      return;
    }

    amountModule.forEach(_element => {
      const element = _element;

      // show favorite amount badge
      element.classList.remove('hidden');

      // display favorite list array length
      element.innerHTML = favoriteListArray.length;
    });
  }

  // shows the favorite toggles in the product list items or on detail page
  renderFavoriteToggles() {
    const { toggleModuleList } = this.config;

    if (!toggleModuleList()) {
      return;
    }

    toggleModuleList().forEach(element => {
      // check if toggle needs to be active on first load
      this.initialStatus(element);

      // show favorite toggle to prevent flicker from first api call
      element.classList.remove('hidden');

      // set event listener on toggle icon
      this.handleClick(element);
    });
  }

  // checks if element is inside favoriteListArray, then set as active item
  initialStatus(_element) {
    const element = _element;
    const { favoriteListArray } = this.config;
    const { productItemcode } = element.dataset;

    if (!productItemcode) {
      return;
    }

    if (favoriteListArray.includes(productItemcode)) {
      element.classList.add('is-active');
      element.dataset.tooltip = element.dataset.removeTooltip || 'Remove from favorites';
    }
  }

  // handle the onclick of the favorite toggle
  handleClick(_element) {
    const element = _element;
    const { productItemcode } = element.dataset;

    if (!productItemcode) {
      return;
    }

    element.addEventListener('click', event => {
      event.preventDefault();

      if (element.classList.contains('is-active')) {
        this.removeFavorite(productItemcode, element);
      } else {
        this.addFavorite(productItemcode, element);
      }

      // trigger favorite amount to display new value
      this.renderFavoriteAmount();
    });
  }

  init() {
    const { token } = this.config;

    const initialSetup = () => {
      // if my account favorite module is present, render the product list
      this.renderFavoriteProductList();

      // if any product favorite toggle module is present, render render the handler
      this.renderFavoriteToggles();

      // counts favorite list and display amount in navbar
      this.renderFavoriteAmount();
    };

    // check if user is logged in
    if (token) {
      this.getFavorites()
        .then(data => {
          if (data != null) {
            // sets global value to create or update a user preference row in the database
            this.config.hasPreferenceObject = true;
          }
          // merge cookie data and database data
          const mergedList = data
            ? utils.mergeArrays(data, getFavoriteCookie())
            : getFavoriteCookie();

          // if cookie data and database data are not identical, then update the database with the merged data
          if (data != null && data.length !== getFavoriteCookie().length) {
            this.updateFavoritesList(mergedList);
          }

          // update cookie
          setFavoriteCookie(mergedList);

          // set merged arrays as globaly variable
          this.config.favoriteListArray = mergedList;

          // trigger all initial functions
          initialSetup();
        })
        .catch(e => {
          console.error(e);
        });
    } else {
      // get array from cookie and set as globaly variable
      this.config.favoriteListArray = getFavoriteCookie();

      // trigger all initial functions
      initialSetup();
    }
  }
}

export default ProductFavorite;
